import { useContext, useEffect, useState } from "react"
import { Formik, Form } from "formik"
import { FiAlertTriangle, FiArrowRight, FiSend } from "react-icons/fi"
import { addHours, format, isBefore } from "date-fns"
import { toast } from "react-toastify"
import { GlobalContext } from "../../../../contexts/GlobalContext"
import Spinner from "../../../../components/Spinner"
import { Button, Select, Textbox } from "../../../../components/Form"
import Table from "../../../../components/Table"
import { api, authHeaders } from "../../../../services/api"
import Yup from "../../../../services/yup"
import formUtils from "../../../../util/form"

const validation = Yup.object({
	nome: Yup.string().required('Informe o nome.'),
	email: Yup.string().email('E-mail inválido.').required('Informe o e-mail.'),
	funcao: Yup.object({
		label: Yup.string(),
		value: Yup.number()
	}).required('Selecione a função do convidado no projeto.').nullable()
})

export function ConviteProjeto({ projeto, funcoes }) {
	const { user, checkException } = useContext(GlobalContext)

	const [convitesEnviados, setConvitesEnviados] = useState([])
	const [intervaloReenvio, setIntervaloReenvio] = useState(0)

	async function loadIntervaloReenvioConvite() {
		const response = await api.get(`parametro/intervalo_envio_convite_projeto`)

		setIntervaloReenvio(+response.valor)
	}

	async function loadConvitesEnviados() {
		try {
			const response = await api.get(`projeto/${projeto.id}/listar_convites`, authHeaders())

			const emails = response.map(item => item.email)

			const usuarioPorEmail = await api.post('usuario/verificar_por_email', { emails }, authHeaders())

			setConvitesEnviados(response.map(item => {
				const jaEstaNoProjeto = projeto.pessoas.some(pessoa => pessoa.contato[0].contato === item.email)
				const jaTemUsuario = usuarioPorEmail.some(pessoa => pessoa.contato.find(c => c.tipo_contato_id === 6).contato === item.email)

				return {
					id: item.id,
					convidado: item.nome,
					email: item.email,
					condicao: item.funcao.descricao,
					data: format(new Date(item.created_at), 'dd/MM/yyyy HH:mm\'h\''),
					tem_usuario: jaTemUsuario ? 'sim' : 'não',
					incluido_no_projeto: jaEstaNoProjeto ? 'sim' : 'não',
					reenvio_habilitado: isBefore(addHours(new Date(item.created_at), intervaloReenvio), new Date()) && !jaEstaNoProjeto && !jaTemUsuario
				}
			}))
		} catch (e) {
			checkException(e)
		}
	}

	async function handleConvidarPessoa(values, { resetForm }) {
		try {
			values = formUtils.extractFormValues(values)

			console.log('convite', values)

			await api.post(`projeto/${projeto.id}/convidar`, values, authHeaders())

			toast.success('O convite foi enviado por e-mail.')

			loadConvitesEnviados()

			resetForm()
		} catch (e) {
			checkException(e)
		}
	}

	async function handleReenviarConvite(convite) {
		try {
			try {
				await api.post(`projeto/${projeto.id}/reconvidar/${convite.id}`, {}, authHeaders())

				toast.success('O convite foi reenviado por e-mail.')
			} catch (e) {
				checkException(e)
			}
		} catch (e) {
			checkException(e)
		}
	}

	useEffect(() => {
		loadConvitesEnviados()

		loadIntervaloReenvioConvite()
	}, [])

	if (!projeto) {
		return <Spinner />
	}

	const eAutor = projeto.pessoas.some(p => p.id === user.pessoa.id && p.condicao.toLowerCase() === 'autor')

	return (
		<div className="flex flex-col gap-4 pt-8">
			{eAutor ? (
				<>
					<Formik
						onSubmit={handleConvidarPessoa}
						initialValues={{
							nome: '',
							email: '',
							funcao: null
						}}
						validationSchema={validation}
					>
						{({ isSubmitting, setFieldValue, errors }) => (
							<Form className="flex flex-col items-end lg:flex-row w-full">
								<Textbox
									name="nome"
									label="Nome"
								/>

								<Textbox
									name="email"
									label="E-mail"
								/>

								<Select
									name="funcao"
									label="Função"
									onChange={(selected, meta) => {
										setFieldValue(meta.name, selected)
									}}
									options={funcoes.map(funcao => ({
										label: funcao.descricao,
										value: funcao.id
									}))}
								/>

								<Button className="white" style={{ width: 'calc(100% - 16px)' }} type="submit" disabled={!eAutor} loading={isSubmitting}>
									Convidar
									<FiArrowRight size={16} />
								</Button>
							</Form>
						)}
					</Formik>

					{convitesEnviados.length > 0 ? (
						<Table
							caption="Convites enviados"
							headers={[
								{ name: 'convidado', value: 'Nome' },
								{ name: 'email', value: 'E-mail' },
								{ name: 'condicao', value: 'Função', centered: true },
								{ name: 'data', value: 'Enviado em', centered: true },
								{ name: 'tem_usuario', value: 'Cadastrado no sistema', centered: true },
								{ name: 'incluido_no_projeto', value: 'Membro do projeto', centered: true }
							]}
							data={convitesEnviados}
							actions={[
								{
									action: handleReenviarConvite,
									name: 'Reenviar',
									icon: FiSend,
									iconSize: 16,
									checkDisabled: item => !item.reenvio_habilitado,
									disabledTooltip: 'Não é possível enviar convites para esta pessoa no momento.'
								}
							]}
							hideOnSmallHeaders={['incluido_no_projeto', 'tem_usuario', 'data', 'email']}
							hideHeadersOffset={1064}
						/>
					) : null}
				</>
			) : (
				<div className="flex flex-col gap-2 items-center mt-4">
					<FiAlertTriangle size={32} />
					<span>Apenas o autor do projeto pode convidar pessoas.</span>
				</div>
			)}
		</div>
	)
}